import { atom, useAtom } from 'jotai';
import { useCallback } from 'react';
import { FuturesInitialMargin, OrderManagementResource } from '../../resources';
import { FuturesCalculatedCost } from '../../types';
import { Loadable, LoadableState } from '../../utils';
import { ResourceHeadersWithToken } from '../useResource';

const futuresInitialMarginAtom = atom<LoadableState<FuturesInitialMargin>>(Loadable.uninitialized);

type CalculateFuturesCostParams = {
  quantity: number;
  price: number;
};

type UseFuturesInfoCommonProps = {
  orderManagementResource: OrderManagementResource;
  resourceHeadersWithToken: ResourceHeadersWithToken;
};

export const useFuturesInfoCommon = ({
  orderManagementResource,
  resourceHeadersWithToken,
}: UseFuturesInfoCommonProps) => {
  const [futuresInitialMargin, setFuturesInitialMargin] = useAtom(futuresInitialMarginAtom);

  const loadFuturesInitialMargin = useCallback(
    async (ticker: string) => {
      setFuturesInitialMargin(Loadable.loading);
      try {
        const response = await orderManagementResource.getFuturesInitialMargin({ ticker }, resourceHeadersWithToken);
        setFuturesInitialMargin(Loadable.loaded(response.data));
      } catch (error) {
        setFuturesInitialMargin(Loadable.failed);
      }
    },
    [orderManagementResource, resourceHeadersWithToken, setFuturesInitialMargin],
  );

  const calculateFuturesCost = useCallback(
    ({ price, quantity }: CalculateFuturesCostParams): FuturesCalculatedCost | undefined => {
      if (!futuresInitialMargin || !futuresInitialMargin.isLoaded) {
        return undefined;
      }

      return {
        marginRequirementSum: quantity * futuresInitialMargin.futuresInitialMargin,
        netValueSum: price * quantity * futuresInitialMargin.leverage,
      };
    },
    [futuresInitialMargin],
  );

  return {
    loadFuturesInitialMargin,
    calculateFuturesCost,
    futuresInitialMargin,
  };
};
